import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import Select from 'react-select';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { DeleteNucleo, GetNucleo, GetNucleos, SetNucleo, } from '@/modules/structure/nucleo/nucleo.service';
import { GetDiretorias } from '@/modules/structure/diretoria/diretoria.service';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { FormataTelefone } from '@/shared/utils/telefones';
import { GetPredios } from '@/modules/structure/predio/predio.service';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabelas } from '@/shared/constants/tables';
import { readComarca } from '@/modules/structure/comarca/comarca.service';
import { toast } from 'react-toastify';
import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ReadComarcaDto, ReadNucleoDto, ReadPredioDto } from '@/api.types';
import type { CreateNucleoDto, UpdateNucleoDto } from '@/api.types';
import { nucleoFormSchema } from '@/modules/structure/nucleo/nucleo.schema';
import {
  FieldError,
  formControlClass,
  formSelectClass,
  getNestedFieldError,
  hasNestedFieldError,
} from '@/shared/components/forms/FormFieldFeedback';
import { normalizeNucleoFormFromApi } from '@/modules/structure/nucleo/nucleo-form.utils';
import { numberInputValueAs } from '@/shared/utils/formZod.utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useForm,
  Controller,
  SubmitHandler,
  useFormState,
  type FieldErrors,
  type Resolver,
} from 'react-hook-form';

const selectInvalidStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    borderColor: '#dc3545',
    boxShadow: '0 0 0 0.25rem rgba(220, 53, 69, 0.25)',
  }),
};

interface IOpcao { value: number; label: string; }

export default function NucleoEditPage() {
  const { setActions } = useBreadcrumbActions();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  const [comarcas, setComarcas] = useState<ReadComarcaDto[]>([]);
  const [predios, setPredios] = useState<ReadPredioDto[]>([]);
  const [nucleos, setNucleos] = useState<IOpcao[]>([]);
  const [diretorias, setDiretorias] = useState<IOpcao[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    resetField,
  } = useForm<ReadNucleoDto>({
    resolver: zodResolver(nucleoFormSchema as never) as Resolver<ReadNucleoDto>,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      id: 0,
      ordem: 0,
      nome: '',
      substituto1: undefined,
      substituto2: undefined,
      telefone: { ddd: 79, numero: '' },
      resolucao: '',
      maximoIntegrantes: 0,
      maximoMembros: 0,
      diretoria: undefined,
      comarca: undefined,
      predio: undefined,
    },
  });
  const { errors } = useFormState({ control });

  const editadoPor = watch('editadoPor');
  const editadoEm = watch('editadoEm');

  const handleNovo = useCallback(() => {
    // Primeiro navega, depois reseta quando o componente detectar id === 0
    navigate(Links.Nucleo_Criar);
  }, [navigate]);

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', onClick: handleNovo },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions, handleNovo]);

  // Reseta o formulário quando o id mudar para 0 (navegação para página de criação)
  useEffect(() => {
    if (id === 0) {
      // Reseta campos controlados individualmente primeiro
      resetField('substituto1', { defaultValue: undefined });
      resetField('substituto2', { defaultValue: undefined });
      resetField('diretoria', { defaultValue: undefined });
      
      // Reseta o formulário completo com valores explícitos
      const resetValues: ReadNucleoDto = {
        id: 0,
        ordem: 0,
        nome: '',
        comarca: comarcas.length > 0 ? { id: comarcas[0].id } as ReadComarcaDto : undefined,
        predio: predios.length > 0 ? { id: predios[0].id } as ReadPredioDto : undefined,
        substituto1: undefined,
        substituto2: undefined,
        telefone: { ddd: 79, numero: '' },
        resolucao: '',
        maximoIntegrantes: 0,
        maximoMembros: 0,
        diretoria: undefined,
      } as ReadNucleoDto;
      
      // Usa reset com todas as opções para garantir limpeza completa
      reset(resetValues, { 
        keepDefaultValues: false,
        keepValues: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false
      });
    }
  }, [id, location.pathname, comarcas, predios, reset, resetField]);

  useEffect(() => {
    Promise.all([
      readComarca({ skip: 0, take: 1000 }).then((result) => {
        setComarcas(result[0]);
      }),
      GetPredios(0, 1000).then((result) => {
        setPredios(result.objects);
      }),
      GetNucleos(0, 1000).then((result) => {
        const resultado = result.objects.sort(function (a, b) {
          if (a.nome! > b.nome!) return 1;
          if (b.nome! > a.nome!) return -1;
          return 0;
        });
        setNucleos(
          resultado.map((item) => {
            return {
              value: item.id,
              label: item.nome,
            };
          }),
        );
      }),
      GetDiretorias(0, 1000).then((result) => {
        setDiretorias(
          result.objects.map((item) => {
            return { value: item.id, label: item.nome };
          }),
        );
      }),
    ])
      .then(() => setLoaded(true))
      .catch((res) => {
        toast.error((res as string) + '');
      });
  }, []);

  useEffect(() => {
    if (loaded && id > 0) {
      GetNucleo(id)
        .then((result) => {
          if (result) {
            const normalized = normalizeNucleoFormFromApi(result);
            if (normalized.telefone?.numero) {
              normalized.telefone.numero = FormataTelefone(normalized.telefone.numero);
            }
            reset(normalized);
          }
        })
        .catch((res) => {
          toast.error((res as string) + '');
        });
    }
  }, [loaded, id, reset]);

  const handleChangeTelefoneNumero = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('telefone.numero', FormataTelefone(e.target.value), { shouldValidate: true });
  };

  const onInvalid = (fieldErrors: FieldErrors<ReadNucleoDto>) => {
    if (import.meta.env.DEV) {
      console.warn('[NucleoEdit] Validação:', fieldErrors);
    }
    toast.error('Corrija os campos destacados antes de salvar.');
  };

  const onSubmit: SubmitHandler<ReadNucleoDto> = (data) => {
    setIsLoading(true);

    const nomeTrimmed = (data.nome || '').trim();
    const resolucaoTrimmed = data.resolucao ? (String(data.resolucao || '').trim()) : undefined;
    const numeroTelefoneTrimmed = data.telefone?.numero ? (String(data.telefone.numero || '').trim()) : undefined;
    
    const dataToSave: CreateNucleoDto | UpdateNucleoDto = {
      id: id > 0 ? id : undefined,
      ordem: Number(data.ordem) || 0,
      nome: nomeTrimmed,
      comarca: data.comarca?.id ? { id: Number(data.comarca.id) } : undefined,
      predio: data.predio?.id ? { id: Number(data.predio.id) } : undefined,
      substituto1: data.substituto1?.id ? { id: Number(data.substituto1.id) } : undefined,
      substituto2: data.substituto2?.id ? { id: Number(data.substituto2.id) } : undefined,
      telefone: data.telefone && (data.telefone.ddd || numeroTelefoneTrimmed) ? {
        ddd: data.telefone.ddd ? Number(data.telefone.ddd) : undefined,
        numero: numeroTelefoneTrimmed || undefined,
      } : undefined,
      resolucao: resolucaoTrimmed || undefined,
      maximoIntegrantes: data.maximoIntegrantes !== undefined && data.maximoIntegrantes !== null ? Number(data.maximoIntegrantes) : 0,
      maximoMembros: data.maximoMembros !== undefined && data.maximoMembros !== null ? Number(data.maximoMembros) : 0,
      diretoria: data.diretoria?.id ? { id: Number(data.diretoria.id) } : undefined,
    };

    SetNucleo(dataToSave)
      .then((res) => {
        toast.success(res + '');
        navigate(`${Links.Nucleo_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setModalExcluirShow(false);
    DeleteNucleo(id)
      .then((res) => {
        toast.success(res + '');
        navigate(`${Links.Nucleo_Index}`);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3>Núcleos</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form key={`form-${id}-${location.pathname}`} onSubmit={(event) => { void handleSubmit(onSubmit, onInvalid)(event); }}>
        <div className="row">
          <div className="mb-3 col-md-1">
            <label htmlFor="ordem" className="form-label">
              Ordem:{' '}
            </label>
            <input
              title="Ordem no relatório"
              type="number"
              className={formControlClass(!!errors.ordem)}
              min={0}
              {...register('ordem', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.ordem?.message} />
          </div>

          <div className="mb-3 col-md-7">
            <label htmlFor="nome" className="form-label">
              Nome Núcleo: <span className="text-danger">*</span>
            </label>
            <input
              id="nome"
              title="Nome"
              type="text"
              className={formControlClass(!!errors.nome)}
              {...register('nome')}
            />
            <FieldError message={errors.nome?.message} />
          </div>

          <div className="mb-3 col-md-2">
            <label htmlFor="maximoIntegrantes" className="form-label">
              Máximo Estagiários: <span className="text-danger">*</span>
            </label>
            <input
              id="maximoIntegrantes"
              title="Estagiários no núcleo"
              type="number"
              className={formControlClass(!!errors.maximoIntegrantes)}
              min={0}
              max={99}
              {...register('maximoIntegrantes', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.maximoIntegrantes?.message} />
          </div>

          <div className="mb-3 col-md-2">
            <label htmlFor="maximoMembros" className="form-label">
              Máximo Membros: <span className="text-danger">*</span>
            </label>
            <input
              id="maximoMembros"
              title="Defensores no núcleo"
              type="number"
              className={formControlClass(!!errors.maximoMembros)}
              min={0}
              max={99}
              {...register('maximoMembros', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.maximoMembros?.message} />
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="substituto1" className="form-label">
              1ª Substituto:{' '}
            </label>
            <Controller
              key={`substituto1-${id}`}
              name="substituto1"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    id="substituto1"
                    isClearable
                    options={nucleos}
                    value={
                      field.value?.id
                        ? nucleos.find((a) => a.value === field.value?.id) || null
                        : null
                    }
                    onChange={(e) => {
                      field.onChange(e ? ({ id: e.value } as ReadNucleoDto) : undefined);
                    }}
                    onBlur={field.onBlur}
                    styles={
                      hasNestedFieldError(errors, 'substituto1')
                        ? selectInvalidStyles
                        : undefined
                    }
                  />
                  <FieldError
                    message={
                      getNestedFieldError(errors, 'substituto1')?.message
                      ?? getNestedFieldError(errors, 'substituto1.id')?.message
                    }
                  />
                </>
              )}
            />
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="substituto2" className="form-label">
              2° Subsitituto:{' '}
            </label>
            <Controller
              key={`substituto2-${id}`}
              name="substituto2"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    id="substituto2"
                    isClearable
                    options={nucleos}
                    value={
                      field.value?.id
                        ? nucleos.find((a) => a.value === field.value?.id) || null
                        : null
                    }
                    onChange={(e) => {
                      field.onChange(e ? ({ id: e.value } as ReadNucleoDto) : undefined);
                    }}
                    onBlur={field.onBlur}
                    styles={
                      hasNestedFieldError(errors, 'substituto2')
                        ? selectInvalidStyles
                        : undefined
                    }
                  />
                  <FieldError
                    message={
                      getNestedFieldError(errors, 'substituto2')?.message
                      ?? getNestedFieldError(errors, 'substituto2.id')?.message
                    }
                  />
                </>
              )}
            />
          </div>
          <div className="mb-3 col-md-4">
            <label htmlFor="comarca" className="form-label">
              Comarca: <span className="text-danger">*</span>
            </label>
            <select title="Comarca" className={formSelectClass(!!errors.comarca?.id)} id="comarca" {...register('comarca.id')}>
              {comarcas.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome}
                </option>
              ))}
            </select>
            <FieldError message={errors.comarca?.id?.message} />
          </div>
          <div className="mb-3 col-md-4">
            <label htmlFor="predio" className="form-label">
              Unidade: <span className="text-danger">*</span>
            </label>
            <select title="Prédio" className={formSelectClass(!!errors.predio?.id)} id="predio" {...register('predio.id')}>
              {predios.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome}
                </option>
              ))}
            </select>
            <FieldError message={errors.predio?.id?.message} />
          </div>

          <div className="mb-3 col-md-6">
            <label htmlFor="diretoria" className="form-label">
              Diretoria:{' '}
            </label>
            <Controller
              key={`diretoria-${id}`}
              name="diretoria"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    id="diretoria"
                    isClearable
                    options={diretorias}
                    value={
                      field.value?.id
                        ? diretorias.find((a) => a.value === field.value?.id) || null
                        : null
                    }
                    onChange={(e) => {
                      field.onChange(e ? ({ id: e.value } as { id: number }) : undefined);
                    }}
                    onBlur={field.onBlur}
                    styles={
                      hasNestedFieldError(errors, 'diretoria')
                        ? selectInvalidStyles
                        : undefined
                    }
                  />
                  <FieldError
                    message={
                      getNestedFieldError(errors, 'diretoria')?.message
                      ?? getNestedFieldError(errors, 'diretoria.id')?.message
                    }
                  />
                </>
              )}
            />
          </div>

          <div className="mb-3 col-md-4">
            <label htmlFor="resolucao" className="form-label">
              Resolução:
            </label>
            <input
              id="resolucao"
              title="Resolução"
              type="text"
              className={formControlClass(!!errors.resolucao)}
              {...register('resolucao')}
            />
            <FieldError message={errors.resolucao?.message} />
          </div>

          <div className="col-md-4 p-3">
            <div className="card">
              <div className="card-body row">
                <div className="mb-3 col-md-4">
                  <label htmlFor="ddd" className="form-label">
                    DDD:
                  </label>
                  <input
                    title="DDD"
                    type="number"
                    className={formControlClass(!!errors.telefone?.ddd)}
                    min={0}
                    max={99}
                    {...register('telefone.ddd', { setValueAs: numberInputValueAs })}
                  />
                  <FieldError message={errors.telefone?.ddd?.message} />
                </div>
                <div className="mb-3 col-md-8">
                  <label htmlFor="numero" className="form-label">
                    Telefone:
                  </label>
                  <input
                    title="Númnero Telefone"
                    type="text"
                    className={formControlClass(!!errors.telefone?.numero)}
                    maxLength={10}
                    {...register('telefone.numero', { onChange: handleChangeTelefoneNumero })}
                  />
                  <FieldError message={errors.telefone?.numero?.message} />
                </div>
              </div>
            </div>
          </div>

          <EditadoPorRodape dados={{ editadoPor, editadoEm }} />

          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      ></ModalExcluir>
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.nucleo}
        idRegistro={id}
      ></ModalHistorico>
    </>
  );
}
